import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Box,
    Button,
    Center,
    Checkbox,
    Flex,
    Icon,
    Spacer,
    Spinner,
    Table,
    TableCaption,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
} from "@chakra-ui/react"
import { Card } from "component/Card"
import { MotionBox } from "component/Motion"
import { PerpLink } from "component/PerpLink"
import { StorageDataMap } from "constant/storage"
import { useAtom } from "jotai"
import { atomWithStorage } from "jotai/utils"
import { EventName } from "module/analytics/types"
import { useCollateralStrings } from "module/vault/hook/useCollateralStrings"
import { useVaultStrings } from "module/vault/hook/useVaultStrings"
import { useWalletStrings } from "module/wallet/hook/useWalletStrings"
import { RiQuestionLine } from "react-icons/ri"
import { useLocalStorage } from "react-use"
import useMeasure from "react-use-measure"
import { scrollbarStyle } from "theme/scrollbarStyle"
import { CollateralActions } from "./CollateralActions"
import { CollateralRow } from "./CollateralRow"

const { HIDE_WALLET_BALANCE, LOW_USD_TIP } = StorageDataMap

const showBridgeUsdcTipAtom = atomWithStorage("showBridgeUsdcTip", true)

function CollateralCard() {
    // TODO: move these into useCollateral
    const { balanceEthStr } = useWalletStrings()
    const { isSettlementNegative } = useVaultStrings()

    const { collateralStrList } = useCollateralStrings()

    // handle hide wallet balance in local storage
    const [isWalletBalanceHidden, setIsWalletBalanceHidden] = useLocalStorage(
        HIDE_WALLET_BALANCE.name,
        HIDE_WALLET_BALANCE.defaultValue,
    )

    // handle low USDC (not negative, just low or zero) warning in local storage
    const [showLowUsdTip, setShowLowUsdTip] = useLocalStorage(LOW_USD_TIP.name, LOW_USD_TIP.defaultValue)
    const [showBridgeUsdcTip, setShowBridgeUsdcTip] = useAtom(showBridgeUsdcTipAtom)
    // observe card width
    const [ref, bounds] = useMeasure()
    const width = bounds.width

    const currentSize = width > 608 ? "md" : "sm"

    //Get USDC deposit amount
    const collateralUSDC = collateralStrList?.find(({ symbol }) => symbol === "USDC")
    const amountUSDC = collateralUSDC?.vaultBalanceStr ?? ""

    return (
        <Card px={0} py={2} pb={4} position="relative" overflowX="auto" sx={scrollbarStyle}>
            <div style={{ width: "100%", height: 10, opacity: 0, background: "red" }} ref={ref} />
            {!collateralStrList && (
                <Center position={"absolute"} left={0} right={0} top={0} bottom={0}>
                    <Spinner />
                </Center>
            )}
            <MotionBox
                initial={{ opacity: 0, minHeight: "200px" }}
                animate={{
                    opacity: collateralStrList?.length === 0 ? 0 : 1,
                    minHeight: collateralStrList?.length === 0 ? "200px" : "auto",
                }}
            >
                <Table size={currentSize} variant="simple">
                    <TableCaption placement="top" display={isSettlementNegative ? "table-caption" : "none"}>
                        <Alert
                            display={isSettlementNegative ? "flex" : "none"}
                            status="warning"
                            mb={4}
                            flexDirection={width < 900 ? "column" : "row"}
                        >
                            <AlertIcon />
                            <AlertTitle>Withdrawal Disabled</AlertTitle>
                            <AlertDescription textAlign={width < 900 ? "center" : "left"}>
                                USDC balance is in debt and the withdrawal is disabled temporarily. Please deposit
                                enough USDC to repay the debt, and withdrawal will be enabled again.{" "}
                                <PerpLink
                                    href="https://support.perp.com/hc/en-us/articles/5257432076569#heading-5"
                                    trackingProps={{ eventName: EventName.LINK_LEARN_MORE_USDC_IS_TOO_LOW }}
                                >
                                    <Text as="span" color="perpetual.500">
                                        Learn more ↗
                                    </Text>
                                </PerpLink>
                            </AlertDescription>
                        </Alert>
                    </TableCaption>
                    <TableCaption placement="bottom">
                        <VStack spacing={6} w="full">
                            {/* Show settlement token deposit/withdrawal button by default in mobile */}
                            {collateralUSDC && currentSize === "sm" && (
                                <CollateralActions isDisabled={isSettlementNegative} data={collateralUSDC} />
                            )}
                            <Flex w="full" flexDirection={["column", "row"]} align={["flex-start", "center"]}>
                                <Checkbox
                                    display={currentSize === "sm" ? "none" : "flex"}
                                    mr={8}
                                    my={{ base: 2, sm: 0 }}
                                    size="sm"
                                    isChecked={isWalletBalanceHidden !== "false"}
                                    onChange={() =>
                                        setIsWalletBalanceHidden(isWalletBalanceHidden !== "false" ? "false" : "true")
                                    }
                                >
                                    Hide wallet balance
                                </Checkbox>

                                <Spacer />
                                <PerpLink
                                    href="https://support.perp.com/hc/en-us/articles/5257432076569"
                                    trackingProps={{ eventName: EventName.LINK_SUPPORT }}
                                >
                                    <Text color="perpetual.500" fontSize={"sm"} as="span">
                                        Learn more about Multi-collateral ↗
                                    </Text>
                                </PerpLink>
                            </Flex>
                        </VStack>
                    </TableCaption>
                    <Thead whiteSpace="nowrap">
                        <Tr>
                            <Th>Coin</Th>
                            <Th
                                hidden={isWalletBalanceHidden !== "false"}
                                display={currentSize === "sm" ? "none" : undefined}
                            >
                                Wallet
                            </Th>
                            <Th>
                                Amount{" "}
                                <Tooltip hasArrow label="The amount deposited and the collateral value.">
                                    <Box as="span">
                                        <Icon
                                            position="relative"
                                            top="4px"
                                            ml={"4px"}
                                            opacity="1"
                                            boxSize="16px"
                                            as={RiQuestionLine}
                                        />
                                    </Box>
                                </Tooltip>
                            </Th>
                            <Th display={currentSize === "sm" ? "none" : undefined}>WITHDRAWABLE</Th>
                            <Th isNumeric>{currentSize === "sm" ? "More" : "Action"}</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        <Tr display={showBridgeUsdcTip ? "table-row" : "none"}>
                            <Td colSpan={5}>
                                <Alert status="info" flexDirection={width < 900 ? "column" : "row"}>
                                    <AlertIcon />
                                    <AlertDescription textAlign={width < 900 ? "center" : "left"}>
                                        Don't see your USDC? You may need to{" "}
                                        <PerpLink href="https://app.uniswap.org/tokens/optimism/0x7f5c764cbc14f9669b88837ca1490cca17c31607">
                                            <Text color="perpetual.500" as="span">
                                                swap for USDC.e (Bridged USDC)
                                            </Text>
                                        </PerpLink>{" "}
                                        on Uniswap. <br />
                                        <PerpLink href="https://support.perp.com/hc/en-us/articles/25236572820121">
                                            <Text color="perpetual.500" as="span">
                                                Learn more ↗
                                            </Text>
                                        </PerpLink>
                                    </AlertDescription>
                                    <Spacer />
                                    <Button
                                        mt={width < 900 ? 4 : 0}
                                        onClick={() => {
                                            setShowBridgeUsdcTip(false)
                                        }}
                                        borderRadius={"full"}
                                        size={"sm"}
                                    >
                                        Don't show again
                                    </Button>
                                </Alert>
                            </Td>
                        </Tr>
                        <Tr display={showLowUsdTip === "true" && amountUSDC === "0.0" ? "table-row" : "none"}>
                            <Td colSpan={5}>
                                <Alert status="info" flexDirection={width < 900 ? "column" : "row"}>
                                    <AlertIcon />
                                    <AlertDescription textAlign={width < 900 ? "center" : "left"}>
                                        It is recommended to deposit some USDC to avoid{" "}
                                        <PerpLink
                                            href="https://support.perp.com/hc/en-us/articles/5257432076569#heading-3"
                                            trackingProps={{ eventName: EventName.LINK_LEARN_MORE_USDC_IS_TOO_LOW }}
                                        >
                                            <Text color="perpetual.500" as="span">
                                                withdrawal restrictions.
                                            </Text>
                                        </PerpLink>
                                    </AlertDescription>
                                    <Spacer />
                                    <Button
                                        mt={width < 900 ? 4 : 0}
                                        onClick={() => {
                                            setShowLowUsdTip("false")
                                        }}
                                        borderRadius={"full"}
                                        size={"sm"}
                                    >
                                        Don't show again
                                    </Button>
                                </Alert>
                            </Td>
                        </Tr>
                        {collateralStrList?.map(collateralStr => (
                            <CollateralRow
                                key={`collateral-${collateralStr.symbol}`}
                                data={collateralStr}
                                ethAmount={balanceEthStr}
                                isWalletBalanceHidden={isWalletBalanceHidden !== "false"}
                                isUsdcNegative={isSettlementNegative}
                                currentTableBreakpoint={currentSize}
                            />
                        ))}
                    </Tbody>
                </Table>
            </MotionBox>
        </Card>
    )
}

export { CollateralCard }
